/**
 * The image panel on a product.
 *
 * Images are not decoration here — an injected TecDoc article carries them in its
 * `images[]`, so what is uploaded on this panel is what a B2B customer sees on the
 * product page. Hence the two controls that look redundant but are not: order
 * (the first image is the one most consumers render) and the inject toggle (a
 * photo can be useful internally and still be wrong to publish).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useApi } from '../lib/api';
import type { ProductImage } from '../lib/types';
import { Empty, ErrorBox, Loading, Switch, cx } from '../ui';

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function ProductImages({ productId }: { productId: number }) {
  const { request, base } = useApi();
  const [items, setItems] = useState<ProductImage[] | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    const r = await request<{ items: ProductImage[] }>('GET', `/v1/products/${productId}/images`);
    if (!r.ok) {
      setError(typeof r.data === 'object' && r.data && 'error' in r.data ? String(r.data.error) : `HTTP ${r.status}`);
      setItems([]);
      return;
    }
    setError('');
    setItems(r.data.items ?? []);
  }, [productId, request]);

  useEffect(() => {
    void load();
  }, [load]);

  async function upload(files: FileList | null) {
    if (!files || files.length === 0) return;
    setBusy(true);
    const form = new FormData();
    for (const f of Array.from(files)) form.append('file', f);
    const r = await request<{ added: number; errors?: { file: string; error: string }[] }>(
      'POST',
      `/v1/admin/products/${productId}/images`,
      form,
    );
    setBusy(false);
    if (fileRef.current) fileRef.current.value = '';
    if (!r.ok) {
      setError(`upload failed (HTTP ${r.status})`);
      return;
    }
    const failed = r.data.errors ?? [];
    setError(failed.length ? failed.map((f) => `${f.file}: ${f.error}`).join('; ') : '');
    await load();
  }

  async function patch(id: number, body: Record<string, unknown>) {
    await request('PATCH', `/v1/admin/images/${id}`, body);
    await load();
  }

  async function remove(id: number) {
    await request('DELETE', `/v1/admin/images/${id}`);
    await load();
  }

  /** Move an image one slot and persist the whole resulting order. */
  async function move(index: number, delta: number) {
    if (!items) return;
    const next = [...items];
    const to = index + delta;
    if (to < 0 || to >= next.length) return;
    [next[index], next[to]] = [next[to]!, next[index]!];
    setItems(next);
    await request('PUT', `/v1/admin/products/${productId}/images/order`, { ids: next.map((i) => i.id) });
    await load();
  }

  // Images are public (content-addressed) so they need no token on the URL. The
  // API returns an absolute one — the CDN when a pull zone is serving, its own
  // origin otherwise; the fallback is for an API older than that field.
  const src = (img: ProductImage): string =>
    img.url ??
    `${base.replace(/\/$/, '')}/images/${img.sha256.slice(0, 2)}/${img.sha256.slice(2, 4)}/${img.sha256}.${img.ext}`;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          multiple
          className="input"
          style={{ width: 'auto' }}
          onChange={(e) => void upload(e.target.files)}
          disabled={busy}
        />
        {busy && <span className="text-[12px] text-muted">uploading…</span>}
        <span className="ml-auto text-[12px] text-faint">
          the first image is the one an injected article shows first
        </span>
      </div>

      {error && <ErrorBox>{error}</ErrorBox>}

      {items === null ? (
        <Loading />
      ) : items.length === 0 ? (
        <Empty>No images. Upload one, or bulk-import a catalogue folder from Settings.</Empty>
      ) : (
        <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))' }}>
          {items.map((img, i) => (
            <figure
              key={img.id}
              className={cx('flex flex-col gap-2 rounded border border-line bg-panel2 p-2', !img.injected && 'opacity-60')}
            >
              <a href={src(img)} target="_blank" rel="noreferrer" className="block">
                <img
                  src={src(img)}
                  alt={img.caption ?? img.fileName ?? ''}
                  loading="lazy"
                  className="h-32 w-full rounded bg-panel object-contain"
                />
              </a>
              <figcaption className="mono truncate text-[11px] text-faint" title={img.fileName ?? ''}>
                {i === 0 && <span className="chip border-ok text-ok mr-1">primary</span>}
                {img.fileName ?? img.sha256.slice(0, 10)}
              </figcaption>
              <div className="text-[11px] text-faint">
                {img.width && img.height ? `${img.width}×${img.height} · ` : ''}
                {fmtBytes(img.bytes)} · {img.origin}
              </div>
              <input
                className="input text-[12px]"
                placeholder="Caption…"
                defaultValue={img.caption ?? ''}
                onBlur={(e) => {
                  if (e.target.value !== (img.caption ?? '')) void patch(img.id, { caption: e.target.value || null });
                }}
              />
              <div className="flex items-center gap-1">
                <button className="btn btn-sm" disabled={i === 0} onClick={() => void move(i, -1)} title="Move earlier">
                  ←
                </button>
                <button
                  className="btn btn-sm"
                  disabled={i === items.length - 1}
                  onClick={() => void move(i, 1)}
                  title="Move later"
                >
                  →
                </button>
                <div className="ml-auto">
                  <Switch on={img.injected} onChange={(v) => void patch(img.id, { injected: v })}>
                    <span className="text-[11px]">inject</span>
                  </Switch>
                </div>
              </div>
              <button className="btn btn-sm btn-danger" onClick={() => void remove(img.id)}>
                Delete
              </button>
            </figure>
          ))}
        </div>
      )}
    </div>
  );
}
